import React, { useEffect } from 'react';

// Digital HIG Components
// @ts-ignore
import DHContainer from "@digital-hig/mui/Container";
// @ts-ignore
import Typography from "@digital-hig/mui/Typography";
// @ts-ignore
import Box from "@digital-hig/mui/Box";

// Authentication
import { useCfpAuthUtilities } from '../providers/CfpAuthContext';
import logger from '../utils/logger';

interface AccessDeniedProps {
  onBackHome?: () => void;
  message?: string;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ 
  onBackHome: _onBackHome, 
  message = "You don't have permission to access this page. Please contact your organization for access." 
}) => {
  const { cfpAuthUtils, loading } = useCfpAuthUtilities();

  // Logout user when access denied page is shown (if user is authenticated)
  // Following the same pattern as HomePage/GenerateConfigPage for auth checks
  useEffect(() => {
    const performLogout = async () => {
      // Wait for loading to complete (same pattern as other pages)
      if (loading) {
        return;
      }

      // Wait a bit for the auth context to initialize (same pattern as HomePage/GenerateConfigPage)
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check if user is authenticated using same pattern as HomePage/GenerateConfigPage
      // This check prevents logout if user is already logged out (e.g., after redirect)
      const cachedUserInfo = cfpAuthUtils.getCachedUserInfo?.();
      const idToken = cfpAuthUtils.getIdToken?.();
      const isUserAuthenticated = !!cachedUserInfo || !!idToken;

      if (!isUserAuthenticated) {
        logger.info('ℹ️ Access denied page: User is not authenticated, no logout needed');
        return;
      }

      try {
        logger.info('🚫 Access denied - user is authenticated, performing logout with redirect to /access-denied');
        
        // Call logout with custom redirect URI
        // The logout function handles:
        // - CFP provider availability check
        // - Actual logout if user is logged in
        // - Cache clearing
        // - Redirect to /access-denied (handles all edge cases)
        // After redirect, component remounts and auth check will prevent infinite loop
        await cfpAuthUtils.logout(`${window.location.origin}/access-denied`);
      } catch (error) {
        logger.error('❌ Error during logout on access denied page:', error);
      }
    };

    performLogout();
  }, [loading, cfpAuthUtils]);

  return (
    <DHContainer maxWidth="md">
      {/* Spacer: Navigator height (96px) + Gap (104px) = 200px */}
      <Box sx={{ height: '200px' }} />
      
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          backgroundColor: 'transparent'
        }}
      >
          {/* Main Content Container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              width: '640px',
              height: '270px', // Exact height as specified
              maxWidth: '100%'
            }}
          >
            {/* Lock Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                width: '100%',
                marginBottom: '24px' // Explicit 24px gap after icon
              }}
            >
              <img 
                src="/images/lock.svg" 
                alt="Lock"
                style={{
                  width: '100px',
                  height: '100px',
                  opacity: 1,
                  fill: 'rgba(0, 0, 0, 0.00)'
                }}
              />
            </Box>

            {/* Title and Message Container - NO gap between them */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                marginBottom: '24px' // Explicit 24px gap after text content
              }}
            >
              {/* Title */}
              <Typography 
                variant="headline-medium"
                sx={{ 
                  color: '#212121',
                  fontFamily: '"Artifakt Element"',
                  fontSize: '21px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%',
                  textAlign: 'left',
                  width: '100%',
                  marginBottom: '8px' // 8px gap between title and message
                }}
              >
                Access denied
              </Typography>

              {/* Message */}
              <Typography 
                variant="body-copy-medium"
                sx={{ 
                  color: '#212121',
                  fontFamily: '"Artifakt Element"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                  maxWidth: 640,
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                {message}
              </Typography>
            </Box>

          </Box>
      </Box>
    </DHContainer>
  );
};

export default AccessDenied;
